import { useState } from 'react';

import type { CancelToken } from 'axios';

import { API_BASE_URL } from '../config/config';
import {
  ApiResponse_bulkEmailDTO,
  ApiResponse_guid,
  ApiResponse_searchResponseDTO_1,
  BulkEmailDTO,
  SearchRequestDTO_bulkEmailSearchRequestDTO,
  BulkEmailClient,
} from '../services/apiClient';
import axiosInstance from '../services/axiosInstance';
import { handleHookError } from '../utils/hookUtils/handleHookError';

interface UseBulkEmailApi {
  create: (data: BulkEmailDTO) => Promise<ApiResponse_bulkEmailDTO>;
  edit: (id: string, data: BulkEmailDTO) => Promise<ApiResponse_bulkEmailDTO>;
  getById: (id: string) => Promise<ApiResponse_bulkEmailDTO>;
  search: (
    filters: SearchRequestDTO_bulkEmailSearchRequestDTO,
  ) => Promise<ApiResponse_searchResponseDTO_1>;
  remove: (id: string) => Promise<void>;
  processBulkEmail: (id: string) => Promise<void>;
  sendTestEmail: (id: string, testEmailAddress: string) => Promise<void>;
  uploadBulkEmailAttachment: (
    fileName: string | null | undefined,
    body: Blob,
    cancelToken?: CancelToken,
  ) => Promise<ApiResponse_guid>;
  isLoading: boolean;
  error: string | null;
}

export const useBulkEmailApi = (): UseBulkEmailApi => {
  const apiClient = new BulkEmailClient(API_BASE_URL, axiosInstance);

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const create = async (
    data: BulkEmailDTO,
  ): Promise<ApiResponse_bulkEmailDTO> => {
    setIsLoading(true);
    try {
      return await apiClient.createBulkEmail(data);
    } catch (err: unknown) {
      handleHookError(err, 'Error creating bulk email', setError);
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const edit = async (
    id: string,
    data: BulkEmailDTO,
  ): Promise<ApiResponse_bulkEmailDTO> => {
    setIsLoading(true);
    try {
      return await apiClient.updateBulkEmail(id, data);
    } catch (err: unknown) {
      handleHookError(err, 'Error updating bulk email', setError);
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const getById = async (id: string): Promise<ApiResponse_bulkEmailDTO> => {
    setIsLoading(true);
    try {
      return await apiClient.getBulkEmailById(id);
    } catch (err: unknown) {
      handleHookError(err, 'Error fetching bulk email', setError);
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const search = async (
    filters: SearchRequestDTO_bulkEmailSearchRequestDTO,
  ): Promise<ApiResponse_searchResponseDTO_1> => {
    setIsLoading(true);
    try {
      return await apiClient.searchBulkEmails(filters);
    } catch (err: unknown) {
      handleHookError(err, 'Error searching bulk email', setError);
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const remove = async (id: string): Promise<void> => {
    setIsLoading(true);
    try {
      await apiClient.deleteBulkEmail(id);
    } catch (err: unknown) {
      handleHookError(err, 'Error deleting bulk email', setError);
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const processBulkEmail = async (id: string): Promise<void> => {
    setIsLoading(true);
    try {
      await apiClient.processBulkEmail(id);
    } catch (err: unknown) {
      handleHookError(err, 'Error processing bulk email', setError);
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const sendTestEmail = async (id: string, testEmailAddress: string): Promise<void> => {
    setIsLoading(true);
    try {
      await apiClient.sendTestBulkEmail(id, { testEmailAddress });
    } catch (err: unknown) {
      handleHookError(err, 'Error sending test email', setError);
      throw err;
    } finally {
      setIsLoading(false);
    }
  };


  const uploadBulkEmailAttachment = async (
    fileName: string | null | undefined,
    body: Blob,
    cancelToken?: CancelToken,
  ): Promise<ApiResponse_guid> => {
    setIsLoading(true);
    try {
      return await apiClient.uploadBulkEmailAttachment(
        fileName,
        body,
        cancelToken,
      );
    } catch (err: unknown) {
      handleHookError(err, 'Error uploading bulk email attachment', setError);
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  return {
    create,
    edit,
    getById,
    search,
    remove,
    uploadBulkEmailAttachment,
    processBulkEmail,
    sendTestEmail,
    isLoading,
    error,
  };
};
